import React from 'react';

import PackageSelector from '../../components/RegistrationSteps/PackageSelector';
import PersonalInformation from '../../components/RegistrationSteps/PersonalInformation';
import RolesAndPrivileges from '../../components/RegistrationSteps/RolesAndPrivileges';
import BillingInformation from '../../components/RegistrationSteps/BillingInformation';
import PublicConfirmationPage from '../../pages/Confirmation/PublicConfirmationPage';
import AddContactConfirmationPage from '../../pages/Confirmation/AddContactConfirmationPage';
import AdvertisingVas from 'UserRegistration/components/ValueAddedServices/AdvertisingVas';


export type ContextType = 'ADD_CONTACT' | 'PUBLIC_REGISTRATION';

interface CreateInstanceConfigProps{
    portalCode: string,
    contactId: number,
    preselectedGroupId: number,
    preselectedSubscriptionItemId: [],
    purchasingGroupId: number,
    defaultFrequency: any, //Todo: define frequency
    promoCode: string,
    context: ContextType
}

interface SaveButtonDisabledInfoType {
    componentConfig : any,
    registrationData : any,
    formRef : any,
    packageSelectorData : any
}

/**
 * This method creates a new object that is used as the configuration for an 'instance' of 
 * the UserRegistration component. This configuration contains the definition of all known 
 * configurable sections, the configured order of those components in the page and the 
 * current context within the entire METS application (Public Registration, Add Contact, etc).
 * 
 * This method simply creates or initializes the base configuration. Most of the module's configuration 
 * is defined in the object that is returned but some configuration also lives on the server. That means 
 * that a server call will be required to fetch missing config data. So some values returned 
 * by the object from this function will simply have their defaults and still need to be 
 * set and updated afterwards.
 * 
 * @param {object} props
 * @returns {object} The configuration object.
 */
export function createInstanceConfig(props: CreateInstanceConfigProps) {

    return {
        // The list of components that are currently active and useable on the page.
        activeComponents: [],
        // The list of components that may be used during the current context.
        applicableComponents: [],
        // The code for the current portal.
        portalCode: props.portalCode,
        // The ID of an invited contact (to pre-fill some personal info)
        contactId: props.contactId,
        // The definition of each configuration.
        componentDefinition: {
            PublicPackageSelector: {
                component: PackageSelector,
                props: {
                    formikRef: null, // This component has no form.
                    modelKey: 'subscription',
                    preselectedGroupId: props.preselectedGroupId,
                    preselectedSubscriptionItemId: props.preselectedSubscriptionItemId,
                    purchasingGroupId: props.purchasingGroupId,
                    defaultFrequency: props.defaultFrequency,
                    promoCode: props.promoCode,
                    subComponents: [],
                    saveButtonDisabled: (saveButtonInfo : SaveButtonDisabledInfoType) => {

                        const selectedGroupId = saveButtonInfo.registrationData.registrationModel.subscription.selectedGroup.groupId;

                        // must have at least 1 item selected
                        if (saveButtonInfo.registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length === 0) {
                            return true;
                        }

                        // must have a group selected
                        if (!selectedGroupId) {
                            return true;
                        }

                        // if we aren't using the state selector, skip further checks
                        if (!saveButtonInfo.componentConfig.componentDefinition["PublicPackageSelector"].props.showSynchronizedSubscriptionItemSelector) {
                            return false;
                        }

                        let hasOnlyDisabledItems = true;

                        // make sure none of the selected items in the selected group are disabled
                        saveButtonInfo.registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.forEach((selectedItemID:number) => {
                            const enabledItem = saveButtonInfo.registrationData.registrationModel.selectedSubscriptionPackage.subscriptionItems.find((item : any) => (item.id == selectedItemID) && item.enabled);

                            if (enabledItem) {
                                hasOnlyDisabledItems = false;
                                return;
                            }
                        });

                        if ( hasOnlyDisabledItems ) {
                            return true; // do not allow a save and continue if we have disabled items.
                        }

                        // skip this check on initial load
                        if (!saveButtonInfo.packageSelectorData) {
                            return false;
                        }

                        // can we find out selected group in the package data?
                        const selectedGroup = saveButtonInfo.packageSelectorData.availableSubscriptionItemGroups.find((group : any) => group.id == selectedGroupId);
                        
                        if (!selectedGroup) {
                            return true;
                        }
                    }
                },
                sequence: 0
            },
            AddContactPackageSelector: {
                component: PackageSelector,
                props: {
                    formikRef: null, // This component has no form.
                    modelKey: 'subscription',
                    preselectedGroupId: props.preselectedGroupId,
                    preselectedSubscriptionItemId: props.preselectedSubscriptionItemId,
                    defaultFrequency: props.defaultFrequency,
                    promoCode: props.promoCode,
                    saveButtonDisabled: (saveButtonInfo : SaveButtonDisabledInfoType) => {
                        return !saveButtonInfo.registrationData.registrationModel.subscription.selectedGroup.groupId 
                            || (saveButtonInfo.registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length == 0);
                    }
                },
                sequence: 0
            },
            PublicPersonalInformation: {
                component: PersonalInformation,
                props: {
                    formikRef: React.createRef(),
                    modelKey: 'personalInformation',
                    saveButtonDisabled: (saveButtonInfo : SaveButtonDisabledInfoType) => {
                        return !saveButtonInfo.registrationData.registrationModel.personalInformation.termsAndConditionsAccepted 
                            || (saveButtonInfo.formRef.current && saveButtonInfo.formRef.current.isSubmitting);

                    }
                },
                sequence: 0
            },
            AddContactPersonalInformation: {
                component: PersonalInformation,
                props: {
                    formikRef: React.createRef(),
                    modelKey: 'personalInformation',
                    saveButtonDisabled: (saveButtonInfo : SaveButtonDisabledInfoType) => {
                        return saveButtonInfo.formRef.current && saveButtonInfo.formRef.current.isSubmitting;
                    }
                },
                sequence: 0
            },
            RolesAndPrivileges: {
                component: RolesAndPrivileges,
                props: {
                    modelKey: 'roleSelection',
                    saveButtonDisabled: (saveButtonInfo : SaveButtonDisabledInfoType) => {
                        // Disable the button if there are no roles selected.
                        return saveButtonInfo.registrationData.registrationModel.roleSelection.selectedRoleIds.length === 0;
                    }
                },
                sequence: 0
            },
            BillingInformation: {
                component: BillingInformation,
                isBillingComponent: true,
                props: {
                    defaultBillingAddressCountryCode: 'CA',
                    formikRef: React.createRef(),
                    modelKey: 'billingInformation',
                    saveButtonDisabled: (saveButtonInfo : SaveButtonDisabledInfoType) => 
                    saveButtonInfo.formRef.current 
                        && ((saveButtonInfo.componentConfig.componentDefinition.BillingInformation.props.showCreditCardAgreementCheckbox 
                                && !saveButtonInfo.formRef.current.values.creditCardTermsAndConditionsAccepted) 
                            || saveButtonInfo.formRef.current.isSubmitting),
                    showCreditCardAgreementCheckbox: true
                },
                sequence: 0
            }
        },
        currencyCode: 'USD',
        supportPhonenumber: "",
        supplierEmailAsUsername: false,
        isPCEnabled: false, // MERX specific flag that indicates whether or not the Private Construction module is enabled.
        settings: {},
        vasList : {
            "AdvertisingVas" : AdvertisingVas
        },
        // The currently configured confirmation page.
        confirmationPage: (() => {
            switch (props.context) {
                case 'ADD_CONTACT':
                    return AddContactConfirmationPage;
                case 'PUBLIC_REGISTRATION':
                default:
                    return PublicConfirmationPage;
            }
        })(),
        // The context of the current application.
        context: props.context,
        // The i18n namespaces to be used in the current context.
        i18nNamespaces: (() => {

            const namespaces = [props.portalCode, 'DEFAULT'];
            return namespaces;

        })()
    };

}

/**
 * Enables or disables all components that are related to capturing billing data from the user. This 
 * method may be used when billing is no longer required.
 * 
 * @param {object} componentConfig 
 * @param {function} setComponentConfig 
 * @param {boolean} enable 
 */
export function enableOrDisableBillingComponents(componentConfig: any, setComponentConfig: any, enable = false) {

    const componentConfigCopy = Object.assign({}, componentConfig);

    componentConfigCopy.activeComponents = componentConfigCopy.applicableComponents.filter((componentName: string) => 
        !(!enable && componentConfigCopy.componentDefinition[componentName].isBillingComponent));

    setComponentConfig(componentConfigCopy);

}

/**
 * 
 * @param registrationData 
 * @param componentConfig 
 * @param setComponentConfig 
 */
export function updateBillingRequired(registrationData: any, componentConfig: any, setComponentConfig: any) {

    const selectedPackage = registrationData?.registrationModel?.selectedSubscriptionPackage;
    const vasItemIds = registrationData?.registrationModel?.subscription?.vasItemIds || [];
    const secondarySubscription = registrationData?.registrationModel?.selectedSecondarySubscriptions || [];


    if (selectedPackage && selectedPackage.freePackage &&
        vasItemIds.length === 0 &&
        secondarySubscription.length === 0) {
        enableOrDisableBillingComponents(componentConfig, setComponentConfig, false);
        registrationData.metadata.isBillingRequired = false;
    } else {
        enableOrDisableBillingComponents(componentConfig, setComponentConfig, true);
        registrationData.metadata.isBillingRequired = true;
    }

}

/**
 * Determines whether or not a section's save button should be disabled. It checks the 
 * section's own saveButtonDisabled condition as well as any previous sections.
 * 
 * @param {object} registrationData
 * @param {object} componentConfig 
 */
export function isSaveButtonDisabled(registrationData: any, componentConfig: any, packageSelectorData: any) {

    const saveButtonConditions: any[] = [];

    componentConfig.applicableComponents.forEach((componentName: any, index: any) => {
        
        const componentDef = componentConfig.componentDefinition[componentName];

        const saveButtonInfo:SaveButtonDisabledInfoType = {
            componentConfig: componentConfig,
            registrationData: registrationData,
            formRef: componentDef.props.formikRef,
            packageSelectorData: packageSelectorData
        }

        if ((index + 1) <= registrationData.metadata.currentStep) { // Need to increment the index as it is 0 based and step is 1 based index.
            saveButtonConditions.push(() => componentDef.props.saveButtonDisabled(saveButtonInfo));
        }
        
    });

    //Todo: change to a foreach to clean up
    for (let i = 0, l = saveButtonConditions.length; i < l; i++) {
        const result = saveButtonConditions[i]();
        if (result) {
            return true;
        }
    }
 
    return false;

}
